<template>
  <div class="traductions p-6">
    <h1 class="text-3xl font-bold mb-6">Traductions</h1>
    <table v-if="!errorMessage" class="min-w-full bg-white border border-gray-200">
      <thead>
        <tr>
          <th class="py-2 px-4 border-b border-r">Actions</th>
          <th class="py-2 px-4 border-b border-r">Section</th>
          <th class="py-2 px-4 border-b border-r">Language</th>
          <th class="py-2 px-4 border-b border-r">Titre</th>
          <th class="py-2 px-4 border-b">Description</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="translation in translations" :key="translation.id" class="border-b">
          <td class="py-2 px-4 border-r">
            <div class="button-group">
              <button class="bg-blue-500 text-white px-3 py-1 rounded mb-2 w-full"
                @click="updateTranslation(translation.id)">Update</button>
              <button class="bg-red-500 text-white px-3 py-1 rounded w-full"
                @click="deleteTranslation(translation.id)">Delete</button>
            </div>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="translation.reference_id" class="border p-2 w-full"
              @change="updateTranslation(translation.id)">
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">{{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4 border-r" @click="editField(translation.id, 'language_code')">
            <input v-if="translation.editing?.language_code" v-model="translation.language_code" class="border p-2 w-20"
              @blur="saveField(translation.id, 'language_code')" />
            <span v-else>{{ translation.language_code }}</span>
          </td>
          <td class="py-2 px-4 border-r" @click="editField(translation.id, 'titre')">
            <input v-if="translation.editing?.titre" v-model="translation.titre" class="border p-2 w-full"
              @blur="saveField(translation.id, 'titre')" />
            <span v-else>{{ translation.titre }}</span>
          </td>
          <td class="py-2 px-4" @click="editField(translation.id, 'description')">
            <input v-if="translation.editing?.description" v-model="translation.description" class="border p-2 w-full"
              @blur="saveField(translation.id, 'description')" />
            <span v-else>{{ translation.description }}</span>
          </td>
        </tr>
        <tr v-if="translations.length === 0">
          <td colspan="5" class="py-2 px-4 text-center text-gray-400 italic">Aucune traduction existante</td>
        </tr>
        <tr>
          <td class="py-2 px-4 border-r">
            <div class="button-group">
              <button class="bg-green-500 text-white px-3 py-1 rounded mb-2 w-full"
                @click="addNewTranslation">Add</button>
            </div>
          </td>
          <td class="py-2 px-4 border-r">
            <select v-model="newTranslation.reference_id" class="border p-2 w-full">
              <option v-for="section in flattenedSections" :key="section.id" :value="section.id">{{ section.name }}
              </option>
            </select>
          </td>
          <td class="py-2 px-4 border-r">
            <input v-model="newTranslation.language_code" class="border p-2 w-20" />
          </td>
          <td class="py-2 px-4 border-r">
            <input v-model="newTranslation.titre" class="border p-2 w-full" />
          </td>
          <td class="py-2 px-4">
            <input v-model="newTranslation.description" class="border p-2 w-full" />
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="errorMessage" class="text-red-600">{{ errorMessage }}</p>
    <Notification ref="notification" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { sections, translations } from "../../api";
import Notification from "../../components/Notification.vue";

// Interfaces TypeScript
interface Translation {
  id: number;
  reference_id: number;
  language_code: string;
  titre: string;
  description: string;
  editing?: Record<string, boolean>;
}

interface Section {
  id: number;
  name: string;
  subsections?: Section[];
}

interface FlattenedSection {
  id: number;
  name: string;
}

interface NewTranslation {
  reference_id: number;
  language_code: string;
  titre: string;
  description: string;
}

interface NotificationRef {
  showNotification: (message: string, type: string) => void;
}

interface ApiError {
  response?: {
    status: number;
  };
}

export default defineComponent({
  name: "Traductions",
  components: {
    Notification,
  },
  data() {
    return {
      translations: [] as Translation[],
      sections: [] as Section[],
      flattenedSections: [] as FlattenedSection[],
      newTranslation: {
        reference_id: 0,
        language_code: "",
        titre: "",
        description: "",
      } as NewTranslation,
      errorMessage: "",
    };
  },
  mounted() {
    this.fetchAllTranslations();
    this.fetchAllSections();
  },
  methods: {
    async fetchAllTranslations(): Promise<void> {
      try {
        const response = await translations.fetchTranslations();
        if (Array.isArray(response)) {
          this.translations = response.map((translation: Translation) => ({
            ...translation,
            editing: {}
          }));
          this.errorMessage = "";
        } else {
          this.translations = [];
          this.errorMessage = "Erreur inattendue lors de la récupération des traductions.";
        }
      } catch (error: unknown) {
        const apiError = error as ApiError;
        if (apiError.response && apiError.response.status === 404) {
          this.errorMessage = "Erreur 404 : L'endpoint de l'API n'existe pas.";
        } else {
          this.errorMessage = "Erreur lors de la récupération des traductions.";
        }
        console.error("Erreur lors de la récupération des traductions :", error);
        this.translations = [];
      }
    },
    async fetchAllSections(): Promise<void> {
      try {
        this.sections = await sections.fetchSections();
        this.flattenSections();
      } catch (error) {
        console.error("Erreur lors de la récupération des sections :", error);
      }
    },
    flattenSections(): void {
      const flatten = (sections: Section[], result: FlattenedSection[] = []): FlattenedSection[] => {
        sections.forEach(section => {
          result.push({ id: section.id, name: section.name });
          if (section.subsections && section.subsections.length > 0) {
            flatten(section.subsections, result);
          }
        });
        return result;
      };
      this.flattenedSections = flatten(this.sections);
    },
    async updateTranslation(translationId: number): Promise<void> {
      const translation = this.translations.find(t => t.id === translationId);
      if (translation) {
        try {
          await translations.updateTranslation(translationId, translation);
          this.showNotification("Traduction mise à jour avec succès.", "success");
          await this.fetchAllTranslations();
        } catch (error) {
          this.showNotification("Erreur lors de la mise à jour de la traduction.", "error");
        }
      }
    },
    async addNewTranslation(): Promise<void> {
      try {
        await translations.createTranslation(this.newTranslation);
        this.showNotification("Traduction ajoutée avec succès.", "success");
        await this.fetchAllTranslations();
        this.newTranslation = {
          reference_id: 0,
          language_code: "",
          titre: "",
          description: "",
        };
      } catch (error) {
        this.showNotification("Erreur lors de l'ajout de la traduction.", "error");
      }
    },
    async deleteTranslation(translationId: number): Promise<void> {
      try {
        await translations.deleteTranslation(translationId);
        this.showNotification("Traduction supprimée avec succès.", "success");
        await this.fetchAllTranslations();
      } catch (error) {
        this.showNotification("Erreur lors de la suppression de la traduction.", "error");
      }
    },
    showNotification(message: string, type: string): void {
      const notification = this.$refs.notification as NotificationRef;
      notification.showNotification(message, type);
    },
    getSectionName(sectionId: number): string {
      const section = this.flattenedSections.find(s => s.id === sectionId);
      return section ? section.name : 'Unknown';
    },
    editField(translationId: number, field: string): void {
      const translation = this.translations.find(t => t.id === translationId);
      if (translation) {
        if (!translation.editing) {
          translation.editing = {};
        }
        translation.editing[field] = true;
      }
    },
    async saveField(translationId: number, field: string): Promise<void> {
      const translation = this.translations.find(t => t.id === translationId);
      if (translation) {
        translation.editing![field] = false;
        try {
          await translations.updateTranslation(translationId, translation);
          this.showNotification("Traduction mise à jour avec succès.", "success");
        } catch (error) {
          this.showNotification("Erreur lors de la mise à jour de la traduction.", "error");
        }
      }
    },
  },
});
</script>

<style scoped>
.button-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.button-group button {
  width: 100%;
}
</style>